import { Partner, PlatformUser as PlatformUserRaw } from '@provision/entity-types';
import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { isNullish, pathOr, stringToPath } from 'remeda';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  excludePlatformWhenDenied,
  usePlatformCatalogAccess,
} from 'src/graphql/entities/group-rules/platform-catalog-access';
import { PlatformUser, useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import {
  ConditionInput,
  ConditionOperator,
  EntityNames,
  QueryVariables,
  Records,
} from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { CustomViewRaw as CustomViewRawT } from 'src/types';
import { parseJsonSafely } from 'src/utils/objects';
import { OPTIMISTIC_ID_PREFIX } from './constants';
import { useCustomViewAccess } from './custom-view-access';
import { useCustomViewCustomAttributes } from './custom-view-config/custom-attributes-bridge';
import {
  CUSTOM_FILTER_MAPPER,
  CUSTOM_VIEWS_CONFIG,
  CustomViewEntity,
} from './custom-view-config/custom-view-config';
import { customViewOperators, isUnary } from './custom-view-config/operators';
import { mapSortAttributeToFormInterface } from './custom-view-config/utils';
import { handleCustomViewDateFields } from './custom-view-date-field';
import {
  CustomView,
  CustomViewAttribute,
  CustomViewFormConditionItem,
  CustomViewFormModel,
  UIType,
} from './custom-view.types';
import { RULES_MAP_PER_VIEW_ENTITY } from './rules-map-per-view-entity';

type CustomViewRaw = Omit<CustomViewRawT, 'entityName'> & {
  entityName: CustomViewEntity;
};

const MAX_CUSTOM_VIEWS_LIMIT = 500;

const CUSTOM_VIEW_FIELDS = gql`
  fragment CustomViewFields on CustomView {
    id
    customViewId
    entityName
    partnerId
    conditions
    columns
    sort
    name
    description
    isPrivate
    isDefault
    createdAt
    icon
  }
`;

const CUSTOM_VIEW_RELATIONS = gql`
  fragment CustomViewRelations on CustomView {
    id
    createdByUser {
      id
      userId
      firstName
      lastName
    }
  }
`;

const GET_CUSTOM_VIEWS_QUERY = gql`
  query GetAvailableCustomViews($params: GetAvailableCustomViewsInput!)
  @entity(name: "CustomView") {
    getAvailableCustomViews(params: $params) {
      records {
        ...CustomViewFields
        ...CustomViewRelations
      }
    }
  }
  ${CUSTOM_VIEW_FIELDS}
  ${CUSTOM_VIEW_RELATIONS}
`;

const DELETE_CUSTOM_VIEWS_QUERY = gql`
  mutation DeleteCustomView($customViewId: String!) {
    deleteCustomView(
      conditions: [{ attribute: "customViewId", operator: "=", value: $customViewId }]
    ) {
      ...CustomViewFields
    }
  }
  ${CUSTOM_VIEW_FIELDS}
`;

const CREATE_CUSTOM_VIEWS_QUERY = gql`
  mutation CreateCustomView(
    $createdBy: String
    $partnerId: Int
    $conditions: String
    $columns: String
    $sort: String
    $name: String
    $description: String
    $entityName: String
    $isPrivate: Boolean
    $isDefault: Boolean
    $icon: String
  ) {
    createCustomView(
      data: {
        createdBy: $createdBy
        partnerId: $partnerId
        conditions: $conditions
        columns: $columns
        sort: $sort
        name: $name
        description: $description
        entityName: $entityName
        isPrivate: $isPrivate
        icon: $icon
        isDefault: $isDefault
      }
    ) {
      ...CustomViewFields
      ...CustomViewRelations
    }
  }
  ${CUSTOM_VIEW_FIELDS}
  ${CUSTOM_VIEW_RELATIONS}
`;

const UPDATE_CUSTOM_VIEWS_QUERY = gql`
  mutation UpdateCustomView(
    $customViewId: String!
    $partnerId: Int
    $conditions: String
    $columns: String
    $sort: String
    $name: String
    $description: String
    $isPrivate: Boolean
    $isDefault: Boolean
    $icon: String
  ) {
    updateCustomView(
      conditions: [{ attribute: "customViewId", operator: "=", value: $customViewId }]
      data: {
        partnerId: $partnerId
        conditions: $conditions
        columns: $columns
        sort: $sort
        name: $name
        description: $description
        isPrivate: $isPrivate
        isDefault: $isDefault
        icon: $icon
      }
    ) {
      ...CustomViewFields
    }
  }
  ${CUSTOM_VIEW_FIELDS}
`;

export interface GetCustomView {
  getAvailableCustomViews: Records<CustomViewRaw>;
}

export interface DeleteCustomView {
  deleteCustomView: CustomViewRaw[];
}

export interface CreateCustomView {
  createCustomView: CustomViewRaw[];
}

export interface UpdateCustomView {
  updateCustomView: CustomViewRaw[];
}

const isCustomViewEntity = (entity: string | undefined): entity is CustomViewEntity =>
  !!entity && entity in CUSTOM_VIEWS_CONFIG;

export const useCustomViews = (params: {
  partnerId?: number | null;
  entity?: string;
  searchTerm?: string;
}) => {
  const { user } = useCurrentUserProfile();
  const { includePlatformLevelData: includePlatformViews } = usePlatformCatalogAccess(
    'CustomView',
    params.partnerId,
  );

  // fetching all views without filters and pagination to reduce amount of queries since
  // we allow to operate with all of them and total amount of accessible views not going
  // to be big enough to cause problems with approach like that
  const { data, loading, refetch } = useQuery<GetCustomView, QueryVariables>(
    GET_CUSTOM_VIEWS_QUERY,
    {
      context: {
        includePlatformLevelData: includePlatformViews,
      },
      variables: {
        params: {
          limit: MAX_CUSTOM_VIEWS_LIMIT,
        },
      },
    },
  );

  const operations = useCustomViewOperations();

  const { customViewAccessesMap, createCustomViewAccess, updateCustomViewAccess } =
    useCustomViewAccess();

  // saved views store custom-attribute columns/filters/sort by their `customAttribute__*` ids, which
  // are NOT in the static CUSTOM_VIEWS_CONFIG maps so hydrating against config alone silently drops
  // them on reload. Merge the live catalog (display + filter descriptors) for the active entity so
  // those references resolve; attributes the user can no longer access stay absent and are dropped.
  const catalogEntity: CustomViewEntity = isCustomViewEntity(params.entity)
    ? params.entity
    : EntityNames.Conversation;
  const { columnDescriptorsById, filterDescriptorsById } =
    useCustomViewCustomAttributes(catalogEntity);

  // formatting raw custom views into UI friendly schema
  const customViews = useMemo<CustomView[]>(() => {
    const views = data?.getAvailableCustomViews?.records || [];

    return views
      .filter(
        (i) =>
          (!i.isPrivate || i.createdByUser?.userId === user?.userId) &&
          CUSTOM_VIEWS_CONFIG[i.entityName] &&
          i.name.toLocaleLowerCase().includes(params.searchTerm?.toLocaleLowerCase?.() ?? ''),
      )
      .map((view) => {
        const config = CUSTOM_VIEWS_CONFIG[view.entityName];

        // only the active entity's catalog is loaded; merge it so saved custom-attribute
        // columns/filters/sort resolve the same way built-ins do.
        const isActiveEntity = view.entityName === params.entity;
        const attributesMap =
          isActiveEntity && columnDescriptorsById.size > 0
            ? new Map<string, CustomViewAttribute>([
                ...config.attributesMap,
                ...columnDescriptorsById,
              ])
            : config.attributesMap;

        const filterAttributesMap =
          isActiveEntity && filterDescriptorsById.size > 0
            ? new Map([...config.filterAttributesMap, ...filterDescriptorsById])
            : config.filterAttributesMap;

        const foundAccess = customViewAccessesMap.get(view.customViewId);

        const rawConditions: ConditionInput[] = parseJsonSafely(view.conditions) ?? [];

        // to be able to use stored conditions from the database we are replacing attribute values with
        // real gql attribute paths from the configuration map
        const apiConditions = rawConditions
          ?.map((storedData) => {
            const matchedAttribute =
              storedData.attribute && filterAttributesMap?.get(storedData.attribute);

            if (!matchedAttribute) {
              return null;
            }

            return {
              ...storedData,
              attribute: matchedAttribute.accessor,
              // re-derive from the current descriptor (not the stored JSON) so a saved filter uses
              // the definition's live default for the unset-to-default coalesce.
              coalesceDefault: matchedAttribute.coalesceDefault,
            };
          })
          .filter(Boolean) as ConditionInput[];

        // formatting gql like object into form-friendly schema so it can be used consistently in the app
        const formFriendlyRules = rawConditions
          ?.map((apiCondition, idx) => {
            const matchedAttribute = filterAttributesMap?.get(apiCondition.attribute);

            const matchedOperator = customViewOperators.find(
              (operator) => operator.value === (apiCondition.operator as ConditionOperator),
            );

            if (!matchedAttribute || !matchedOperator) {
              return null;
            }

            const rawValue = apiCondition?.value as string;

            // removing extra symbols used specifically for gql api
            let value: CustomViewFormConditionItem['value'] = rawValue?.replace?.(/%/g, '');

            if (matchedAttribute.uiType === UIType.List) {
              const values = rawValue?.split(',');

              value = values
                .map((token) => {
                  return {
                    value: token,
                    id: token,
                    label: token,
                  };
                })
                .filter(Boolean) as CustomViewFormConditionItem['value'];

              if (!matchedOperator?.multi) {
                // @ts-expect-error will be fixed soon with 1V-2328
                value = value?.[0] as CustomViewFormConditionItem['value'];
              }
            }

            return {
              id: `${matchedAttribute.accessor}_${idx}`,
              field: matchedAttribute,
              operator: apiCondition.operator,
              value,
              valueSource: 'value',
            };
          })
          .filter(Boolean) as CustomViewFormConditionItem[];

        const formConditions = {
          combinator: rawConditions[0]?.logicalOperator ?? 'and',
          not: false,
          rules: formFriendlyRules || [],
        };

        const rawSort = (parseJsonSafely(view.sort) ?? {}) as {
          attribute: string;
          desc: boolean;
          aggFunction?: string;
        };

        const sortAttribute = attributesMap.get(rawSort.attribute);

        const sort = sortAttribute
          ? mapSortAttributeToFormInterface(sortAttribute, rawSort)
          : null;

        return {
          ...view,
          isFavorite: foundAccess?.isFavorite ?? false,
          sortOrder:
            foundAccess?.sortOrder ??
            new Date(foundAccess?.createdAt ?? view.createdAt).getTime(),
          sort,
          conditions: rawConditions,
          apiConditions,
          formConditions,
          columns: (parseJsonSafely<CustomViewAttribute[]>(view.columns) ?? [])
            .map((i) => attributesMap.get(i.attribute)!)
            .filter(Boolean),
        };
      });
  }, [
    data?.getAvailableCustomViews?.records,
    user?.userId,
    params.searchTerm,
    params.entity,
    customViewAccessesMap,
    columnDescriptorsById,
    filterDescriptorsById,
  ]);

  const filteredViews = useMemo(() => {
    const allViews = params.partnerId !== PLATFORM_PARTNER_ID;
    const platformAllowed = excludePlatformWhenDenied(includePlatformViews);

    return customViews.filter((i) => {
      if (params.entity && i.entityName !== (params.entity as EntityNames)) {
        return false;
      }
      if (!allViews && params.partnerId !== i.partnerId) {
        return false;
      }
      return platformAllowed(i);
    });
  }, [params.entity, params.partnerId, customViews, includePlatformViews]);

  const toggleFavorite = useCallback(
    (customViewId: string, { isFavorite }: { isFavorite: boolean }) => {
      if (!user?.userId) {
        return;
      }

      const view = filteredViews.find((i) => i.customViewId === customViewId);

      if (!view) {
        return;
      }

      const foundAccess = customViewAccessesMap.get(view?.customViewId);

      if (!foundAccess) {
        createCustomViewAccess({
          customViewId,
          userId: user?.userId,
          partnerId: user?.partnerId,
          isFavorite,
        });
      } else {
        updateCustomViewAccess(foundAccess.customViewAccessId, {
          ...foundAccess,
          // resetting sort order so the view goes to the end of the list based on date value
          sortOrder: null,
          isFavorite: !foundAccess.isFavorite,
        });
      }
    },
    [
      user,
      filteredViews,
      customViewAccessesMap,
      createCustomViewAccess,
      updateCustomViewAccess,
    ],
  );

  const reorderViews = useCallback(
    (orderedViews: CustomView[]) => {
      const viewsToUpdate = orderedViews
        .slice()
        .map((i, idx) => ({ ...i, newSortOrder: idx }))
        .filter((i) => i.sortOrder !== i.newSortOrder);

      if (!viewsToUpdate?.length) {
        return;
      }

      // that's fine to process this in a loop since it will never contain more than a couple of items
      for (const view of viewsToUpdate) {
        const foundAccess = customViewAccessesMap.get(view?.customViewId);

        if (!foundAccess) {
          return createCustomViewAccess({
            customViewId: view.customViewId,
            userId: user?.userId!,
            partnerId: user?.partnerId!,
            isFavorite: true,
          });
        }

        updateCustomViewAccess(foundAccess.customViewAccessId, {
          ...foundAccess,
          isFavorite: true,
          sortOrder: view.newSortOrder,
        });
      }
    },
    [customViewAccessesMap, updateCustomViewAccess, createCustomViewAccess, user],
  );

  return {
    ...operations,
    toggleFavorite,
    reorderViews,
    customViews: filteredViews,
    refetchCustomView: refetch,
    loading,
  };
};

type CustomViewPatch = Pick<
  CustomView,
  | 'icon'
  | 'columns'
  | 'conditions'
  | 'description'
  | 'entityName'
  | 'name'
  | 'isPrivate'
  | 'sort'
>;

function useCustomViewOperations() {
  const { user } = useCurrentUserProfile();

  const [deleteCustomView, { loading: loadingDeleteCustomView }] =
    useMutation<DeleteCustomView>(DELETE_CUSTOM_VIEWS_QUERY, {
      update(cache, { data }) {
        if (!data?.deleteCustomView?.length) return;

        const id = cache.identify({
          __typename: 'CustomView',
          id: data.deleteCustomView[0].customViewId,
        });

        cache.evict({ id });
        cache.gc();
      },
    });

  const [createCustomView, { loading: loadingCreateCustomView }] =
    useMutation<CreateCustomView>(CREATE_CUSTOM_VIEWS_QUERY, {
      update(cache, { data }) {
        if (!data?.createCustomView?.length) return;

        cache.updateQuery<GetCustomView, QueryVariables>(
          {
            query: GET_CUSTOM_VIEWS_QUERY,
            variables: {
              params: {
                limit: MAX_CUSTOM_VIEWS_LIMIT,
                sorting: { attribute: 'createdAt', desc: true },
              },
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getAvailableCustomViews: {
                ...existingData.getAvailableCustomViews,
                records: [
                  {
                    ...data.createCustomView[0],
                    isDefault: false,
                    // we need to refactor platformUser typings a bit, will be done separetely
                    createdByUser: { userId: user!.userId } as PlatformUserRaw,
                  },
                  ...existingData.getAvailableCustomViews.records.filter(
                    (i) =>
                      i.customViewId !== data.createCustomView[0].customViewId &&
                      !i.customViewId.startsWith(OPTIMISTIC_ID_PREFIX),
                  ),
                ],
              },
            };
          },
        );
      },
    });

  const [updateCustomView, { loading: loadingUpdateCustomView }] =
    useMutation<UpdateCustomView>(UPDATE_CUSTOM_VIEWS_QUERY, {
      update(cache, { data }) {
        if (!data?.updateCustomView?.length) return;

        cache.updateQuery<GetCustomView, QueryVariables>(
          {
            query: GET_CUSTOM_VIEWS_QUERY,
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getAvailableCustomViews: {
                ...existingData.getAvailableCustomViews,
                records: existingData.getAvailableCustomViews.records.map((record) =>
                  record.customViewId === data.updateCustomView[0].customViewId
                    ? { ...record, ...data.updateCustomView[0] }
                    : record,
                ),
              },
            };
          },
        );
      },
    });

  const updateWithOptimisticResponse = useCallback(
    (
      viewToBeUpdated: Pick<CustomView, 'id' | 'partnerId'>,
      patch: CustomViewPatch,
      onCompleted?: QueryHookOptions['onCompleted'],
    ) => {
      const update = formatCustomViewDataForApi({
        ...patch,
        partnerId: viewToBeUpdated.partnerId,
        createdBy: user?.userId!,
      });

      void updateCustomView({
        variables: {
          ...update,
          customViewId: viewToBeUpdated.id,
        },
        optimisticResponse: {
          updateCustomView: [
            {
              ...update,
              id: viewToBeUpdated.id,
              partnerId: viewToBeUpdated.partnerId,
              customViewId: viewToBeUpdated.id,
              isDefault: false,
              createdAt: new Date().toISOString(),
              createdBy: user?.userId!,
              createdByUser: { userId: user!.userId } as PlatformUserRaw,
              partner: {} as Partner,
              customViewAccess: [],
              labelAssignments: [],
            },
          ],
        },
        onCompleted,
        context: {
          omitIsolationModeHeader: true,
        },
      });
    },
    [updateCustomView, user],
  );

  const createWithOptimisticResponse = useCallback(
    (data: CustomViewPatch & Pick<CustomView, 'partnerId'>) => {
      if (isNullish(data.partnerId) || !user) return;

      const create = formatCustomViewDataForApi({
        ...data,
        createdBy: user?.userId,
      });

      void createCustomView({
        variables: create,
        optimisticResponse() {
          const tempId = `${OPTIMISTIC_ID_PREFIX}${new Date().toISOString()}`;

          return {
            createCustomView: [
              {
                __typename: 'CustomView',
                ...create,
                id: tempId,
                customViewId: tempId,
                isDefault: false,
                createdAt: new Date().toISOString(),
                createdByUser: { userId: user.userId } as PlatformUserRaw,
                partner: {} as Partner,
                customViewAccess: [],
                labelAssignments: [],
                dynamicAttributes: [],
                sort: '',
              },
            ],
          };
        },
      });
    },
    [createCustomView, user],
  );

  const deleteWithOptimisticResponse = useCallback(
    async (id: string) => {
      await deleteCustomView({
        variables: {
          customViewId: id,
        },
        optimisticResponse: {
          deleteCustomView: [
            {
              id,
              customViewId: id,
              name: '',
              description: '',
              icon: '',
              partnerId: user?.partnerId!,
              entityName: '' as CustomViewEntity,
              columns: '',
              conditions: '',
              isDefault: false,
              isPrivate: false,
              createdAt: new Date().toISOString(),
              createdBy: user?.userId!,
              createdByUser: { userId: user!.userId } as PlatformUserRaw,
              partner: {} as Partner,
              customViewAccess: [],
              labelAssignments: [],
              sort: '',
            },
          ],
        },
      });
    },
    [deleteCustomView, user],
  );

  const isLoading =
    loadingCreateCustomView || loadingUpdateCustomView || loadingDeleteCustomView;

  return {
    deleteCustomView: deleteWithOptimisticResponse,
    createCustomView: createWithOptimisticResponse,
    updateCustomView: updateWithOptimisticResponse,
    isLoading,
  };
}

export type CustomViewNavItem = CustomView & NavigationDataItem;

interface UseCustomViewMenuItemsParams {
  entityName?: string;
}

export const useCustomViewMenuItems = (
  params?: UseCustomViewMenuItemsParams,
): {
  customViews: CustomView[];
  viewsMenuItems: CustomViewNavItem[];
  reorderViews: (views: CustomViewNavItem[]) => void;
} => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { customViews, reorderViews } = useCustomViews({
    entity: params?.entityName,
    partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
  });

  const viewsMenuItems = useMemo(() => {
    return customViews
      .filter((i) => i.isFavorite)
      .map((i) => {
        const restrictions = RULES_MAP_PER_VIEW_ENTITY[i.entityName] ?? {};

        return {
          ...i,
          ...restrictions,
          iconData: { name: i.icon, library: 'lucide' },
          title: i.name,
          path: portalPaths.entity.selected(i.entityName, i.customViewId),
        } as CustomViewNavItem;
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [customViews]);

  const handleReorder = useCallback(
    (orderedItems: CustomViewNavItem[]) => {
      reorderViews(orderedItems as CustomView[]);
    },
    [reorderViews],
  );

  return { viewsMenuItems, customViews, reorderViews: handleReorder };
};

function formatCustomViewDataForApi(
  view: CustomViewPatch & Pick<CustomView, 'createdBy' | 'partnerId'>,
): Omit<CustomViewPatch, 'columns' | 'conditions' | 'sort'> &
  Pick<CustomViewRaw, 'createdBy' | 'columns' | 'conditions' | 'sort' | 'partnerId'> {
  return {
    ...view,
    sort: view.sort
      ? JSON.stringify({
          attribute: view.sort.attribute,
          desc: view.sort.desc,
          ...(view.sort.aggFunction ? { aggFunction: view.sort.aggFunction } : {}),
        })
      : '',
    conditions: JSON.stringify(view.conditions),
    columns: JSON.stringify(
      // we operate with full CustomViewAttribute object, but want to store only references
      view.columns.map((i) => ({
        attribute: i.attribute,
        order: i.order,
      })),
    ),
  };
}

// Array-typed custom attributes (e.g. multi_select) filter through a Postgres text[]; their accessor
// carries an array cast suffix such as `::text[]`. For those, In/NotIn mean "the stored array
// overlaps / does not overlap the selected values", so the wire operator is swapped to the array
// operators the DB layer understands. Other operators (is_empty/is_not_empty) work unchanged.
const ARRAY_FILTER_ACCESSOR_PATTERN = /::\w+\[\]$/;

// On an array-typed accessor, In/NotIn map to the DB array-overlap operators; anything else
// (is_empty/is_not_empty) passes through. Keyed by the raw wire operator the form produces.
const ARRAY_OPERATOR_BY_LIST_OPERATOR: Record<string, string> = {
  [ConditionOperator.In]: 'array_overlaps',
  [ConditionOperator.NotIn]: 'array_not_overlaps',
};

function resolveArrayFilterOperator(accessor: string | undefined, operator: string): string {
  if (!accessor || !ARRAY_FILTER_ACCESSOR_PATTERN.test(accessor)) {
    return operator;
  }
  return ARRAY_OPERATOR_BY_LIST_OPERATOR[operator] ?? operator;
}

export function mapConditionsForApi(
  group: CustomViewFormModel['conditions'],
  params: { user?: Partial<PlatformUser> | null; interpolateValues?: boolean } = {},
) {
  const list =
    group?.rules
      ?.flatMap((rule) => {
        if (!rule) {
          return null;
        }

        let value = '';
        if (Array.isArray(rule.value)) {
          value = rule.value.map((i) => i?.value).join(',');
          // @ts-expect-error need to find a way for better handling of query-builder typings as its value can be any
        } else if (rule.value?.value) {
          // @ts-expect-error need to find a way for better handling of query-builder typings as its value can be any
          value = rule.value?.value as string;
        } else if (rule.value) {
          value = rule.value as string;
        }

        if (value && ['ilike', 'not_ilike'].includes(rule?.operator as string)) {
          value = `%${value}%`;
        }

        const isUnaryOperator = isUnary(rule.operator);

        const { user, interpolateValues } = params;

        if (!interpolateValues) {
          return [
            {
              attribute: rule.field?.value,
              value: isUnaryOperator ? '' : value,
              operator: rule.operator as string,
              logicalOperator: group.combinator,
            } as ConditionInput,
          ];
        }
        const customFilterMapper =
          CUSTOM_FILTER_MAPPER[rule.field?.rootEntity as keyof typeof CUSTOM_FILTER_MAPPER]?.[
            rule.field?.accessor!
          ];

        if (typeof customFilterMapper === 'function') {
          const filters = customFilterMapper({
            value,
            operator: rule.operator as string,
            logicalOperator: group.combinator,
          });

          return Array.isArray(filters) ? filters : [filters];
        }

        if (rule?.field?.uiType === UIType.Date) {
          return handleCustomViewDateFields(rule);
        }

        return [
          {
            attribute: rule.field?.accessor,
            // it is possible to update operator without clearing the value on UI
            // therefore it is possible that "empty" operators will come with values
            // which is wrong and will throw an error from gql, so simply overriding that
            value: isUnaryOperator ? '' : interpolateValue(value, { user }),
            operator: resolveArrayFilterOperator(
              rule.field?.accessor,
              rule.operator as string,
            ),
            logicalOperator: group.combinator,
            // custom attribute columns: resolve an unset value to the definition default server-side.
            coalesceDefault: rule.field?.coalesceDefault,
          } as ConditionInput,
        ];
      })
      .filter(
        (i) =>
          i?.operator &&
          i.attribute &&
          (i.value || i.operator === 'is_empty' || i.operator === 'is_not_empty'),
      ) ?? [];

  return list as ConditionInput[];
}

/**
 * Replaces placeholders like {{path.to.value}} in a template string with values from a data object
 *
 * @param {string} templateValue - The template string with placeholders
 * @param {object} data - The object containing values, accessed via dot notation
 * @returns {string} - The template with placeholders replaced by values, or an empty string if not found
 */
function interpolateValue(
  templateValue: string,
  data: { user?: Partial<PlatformUser> | null } | null,
) {
  if (typeof templateValue !== 'string' || !data) {
    return templateValue;
  }

  return templateValue.replace(/{{\s*([^{}]+)\s*}}/g, (_, key) => {
    return pathOr(data, stringToPath(key), '') as string;
  });
}

export const filterValidCustomViewFilters = (rules: CustomViewFormConditionItem[]) => {
  return rules.filter(isValidRule) ?? [];
};

function isValidRule(rule: CustomViewFormConditionItem) {
  const hasOperator = rule?.operator;
  const hasFieldValue = rule.field?.value;
  const hasValueOrIsEmptyOperator = !!rule.value || isUnary(rule.operator);

  return hasOperator && hasFieldValue && hasValueOrIsEmptyOperator;
}
